import BodyText from '@lyra/core/components/BodyText'
import TooltipText from '@lyra/core/components/TooltipText'
import formatNumber from '@lyra/core/utils/formatNumber'
import { TokenId } from '@lyra/web/constants/tokens'
import useOrderbookTimestamp from '@lyra/web/hooks/useOrderbookTimestamp'
import useTradingRewards from '@lyra/web/hooks/useTradingRewards'
import { useMemo } from 'react'
import { XStack } from 'tamagui'

type Props = {
  isLoading?: boolean
  fee: number
  // TODO: @earthtojake reimplement omitting base_fee
  baseFee: number
}

export default function TradeFormRewardsRow({ fee, isLoading: isLoadingFees }: Props) {
  const { getTimestamp } = useOrderbookTimestamp()

  const { data: rewards, isLoading: isLoadingRewards } = useTradingRewards()
  const isLoading = isLoadingRewards || !!isLoadingFees

  const round = rewards?.currRound
  const totalBoostedFees = rewards?.tradingStats?.totalBoostedFees ?? 0
  const totalOpRewards = round?.totalTradingRewards[TokenId.OP]?.total ?? 0
  const totalDrvRewards = round?.totalTradingRewards[TokenId.STDRV]?.total ?? 0

  const progress = useMemo(() => {
    if (!round) {
      return 0
    }
    const now = getTimestamp()
    const timeToEnd = round.endTimestamp - now
    const duration = round.endTimestamp - round.startTimestamp
    return duration > 0 ? (duration - timeToEnd) / duration : 0
  }, [round, getTimestamp])

  const projectedFeeShare = totalBoostedFees ? (fee / totalBoostedFees) * progress : 0
  const projectedOpRewards = projectedFeeShare * totalOpRewards
  const projectedDrvRewards = projectedFeeShare * totalDrvRewards

  return (
    <>
      <XStack alignItems="center" justifyContent="space-between" width="100%">
        <TooltipText
          label="stDRV Rewards"
          placement="top-end"
          tooltipContent="Earn a share of stDRV rewards every week for trading fees paid."
        />
        <BodyText color={projectedDrvRewards && !isLoading ? 'cta' : 'primary'}>
          {isLoading ? '...' : `${formatNumber(projectedDrvRewards, { dps: 0 })} stDRV`}
        </BodyText>
      </XStack>
      <XStack alignItems="center" justifyContent="space-between" width="100%">
        <TooltipText
          label="OP Rewards"
          placement="top-end"
          tooltipContent="Earn a share of OP rewards every week for trading fees paid."
        />
        <BodyText color={projectedOpRewards && !isLoading ? 'red' : 'primary'}>
          {isLoading ? '...' : `${formatNumber(projectedOpRewards)} OP`}
        </BodyText>
      </XStack>
    </>
  )
}
